import api from './api';

const LOG_PREFIX = '[match-scheduling]';

/** True when axios/browser cancelled — not the same as HTTP 500. */
export function isMatchSchedulingAbortError(e) {
  const code = String(e?.code || '');
  const name = String(e?.name || '');
  const msg = typeof e?.message === 'string' ? e.message : '';
  const lower = `${code} ${name} ${msg}`.toLowerCase();
  return (
    code === 'ERR_CANCELED' ||
    name === 'CanceledError' ||
    /cancel|cancell?ed|aborted/.test(lower)
  );
}

/** Axios timeout vs fetch abort — still no usable JSON body */
export function isMatchSchedulingLikelyTimeout(e) {
  return e?.code === 'ECONNABORTED' || (typeof e?.message === 'string' && e.message.includes('timeout'));
}

/** Set localStorage.debugMatchScheduling = '1' (or REACT_APP_DEBUG_MATCH_SCHEDULING=true) for every-request traces. */
function matchSchedulingVerbose() {
  if (typeof process !== 'undefined' && process.env.REACT_APP_DEBUG_MATCH_SCHEDULING === 'true') return true;
  try {
    if (typeof window === 'undefined') return false;
    const v = window.localStorage?.getItem('debugMatchScheduling');
    return v === '1' || v === 'true';
  } catch (_) {
    return false;
  }
}

function nowMs() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

/** Compact snapshot — safe to log (no bearer tokens). */
function summarizeSchedulingStatePayload(data) {
  if (!data || typeof data !== 'object') {
    return { parseError: true };
  }
  const lm = data.leagueMatch;
  const open = data.open_round;
  const pending = data.pending_soft_confirmation;
  const slotCount = Array.isArray(data.slots) ? data.slots.length : 0;
  const unresolved =
    !!(
      lm &&
      String(lm.status || '').toLowerCase() === 'scheduled' &&
      !(lm.match_date && lm.match_time)
    );
  return {
    success: !!data.success,
    disabled: !!data.disabled,
    slotCount,
    openRoundNo: open?.round_no ?? null,
    closesAt: open?.closes_at ?? null,
    needsHuman: !!lm?.scheduling_needs_human_help,
    schedulingBlockedCode: data.scheduling_guide?.scheduling_blocked_code ?? null,
    scheduleStatus: lm?.status ?? null,
    calendarDaysInWindow:
      typeof data.scheduling_guide?.calendar_days_in_window === 'number'
        ? data.scheduling_guide.calendar_days_in_window
        : data.scheduling_guide?.calendar_days_in_window ?? null,
    pendingSoftProposal: !!(pending?.proposed_slot && pending?.soft_confirm_until),
    unresolvedFixture: unresolved,
  };
}

/** Human-readable interpretation for the browser console. */
function logSchedulingStateStory(summary, durationMs, verbose) {
  if (!summary || summary.parseError) return;

  const lineBase = `${LOG_PREFIX} state ${Math.round(durationMs)}ms`;
  const awaitingVoteUi = summary.slotCount > 0 || summary.pendingSoftProposal;

  if (summary.disabled) {
    console.warn(lineBase, 'Server has match coordination slots disabled — no voting.');
    return;
  }
  if (!summary.success) {
    console.warn(lineBase, 'Response success:false — check modal error / network tab.');
    return;
  }

  if (verbose) {
    console.info(lineBase, 'snapshot', summary);
  }

  if (summary.slotCount > 0) {
    console.info(
      lineBase,
      `Voting UI: ${summary.slotCount} time option(s) (open round #${
        summary.openRoundNo ?? '?'
      }${summary.closesAt ? `, closes_at ${summary.closesAt}` : ''}).`
    );
    return;
  }

  if (summary.pendingSoftProposal) {
    console.info(
      lineBase,
      'Provisional game time pending soft confirmation (minority grace) — use Contest or wait for countdown.'
    );
    return;
  }

  if (summary.schedulingBlockedCode === 'window_no_schedulable_days') {
    console.warn(
      lineBase,
      'Blocked: window_no_schedulable_days — no calendar days left in play window vs today (server will not propose slots).'
    );
    return;
  }

  if (summary.schedulingBlockedCode === 'max_rounds_exhausted') {
    console.warn(lineBase, 'Blocked: max_rounds_exhausted — auto rounds used up without a locked time.');
    return;
  }

  if (summary.schedulingBlockedCode === 'coordination_required_unknown') {
    console.warn(
      lineBase,
      'Blocked: coordination_required_unknown — coordinator reset or fixture data fix usually required.'
    );
    return;
  }

  if (summary.needsHuman && !awaitingVoteUi && summary.unresolvedFixture) {
    console.warn(
      lineBase,
      'scheduling_needs_human_help=true and 0 slots — server did not open a vote round this response (inspect DB rounds + game window).'
    );
    return;
  }

  if (summary.unresolvedFixture && !awaitingVoteUi) {
    console.warn(
      lineBase,
      'Fixture needs date/time but 0 voting slots returned — orchestration did not expose options (cold DB, error, or still settling; retry).'
    );
  }
}

/** Log slower requests, anomalies, unresolved-without-slots, or everything when verbose. */
function shouldAlwaysLogSchedulingState(summary, ms, errStatus) {
  if (matchSchedulingVerbose()) return true;
  if (errStatus != null || ms >= 5000) return true;
  if (!summary || summary.parseError) return true;
  if (summary.unresolvedFixture && summary.slotCount === 0 && !summary.pendingSoftProposal) return true;
  if (summary.schedulingBlockedCode) return true;
  if (summary.needsHuman && summary.slotCount === 0 && summary.unresolvedFixture) return true;
  return false;
}

const matchSchedulingAPI = {
  getRequestConfig: (teamId) => (teamId ? { params: { team_id: teamId } } : undefined),

  getRequestBody: (payload, teamId) => (teamId ? { ...payload, team_id: teamId } : payload),

  getInbox: async (options = {}) => {
    const t0 = nowMs();
    const verbose = matchSchedulingVerbose();
    try {
      if (verbose) console.info(`${LOG_PREFIX} GET /match-scheduling/inbox ...`);
      const data = await api.get('/match-scheduling/inbox', options);
      const ms = nowMs() - t0;
      if (verbose || ms >= 5000) {
        const n = Array.isArray(data?.matches) ? data.matches.length : 0;
        console.info(`${LOG_PREFIX} inbox ${Math.round(ms)}ms (${n} match row(s))`);
      }
      return data;
    } catch (e) {
      if (!isMatchSchedulingAbortError(e) || verbose) {
        console.error(`${LOG_PREFIX} inbox FAILED ${Math.round(nowMs() - t0)}ms`, e?.response?.status ?? e?.message);
      } else {
        console.debug(`${LOG_PREFIX} inbox cancelled (overlapping request or navigation)`);
      }
      throw e;
    }
  },

  getState: async (matchId, teamId) => {
    const t0 = nowMs();
    const verbose = matchSchedulingVerbose();
    const path = `/match-scheduling/${matchId}/state`;
    try {
      if (verbose) {
        console.info(`${LOG_PREFIX} GET ${path} ... start`, { matchId, teamId });
      }
      const cfg = matchSchedulingAPI.getRequestConfig(teamId) || {};
      const data = await api.get(path, {
        ...cfg,
        /** Match chat used to fire /state every 5s via message poll; keep one slow call from timing out */
        timeout: Number(process.env.REACT_APP_MATCH_SCHEDULING_TIMEOUT_MS || 120000),
      });
      const ms = nowMs() - t0;
      const summary = summarizeSchedulingStatePayload(data);
      if (shouldAlwaysLogSchedulingState(summary, ms, null)) {
        logSchedulingStateStory(summary, ms, verbose);
      }
      return data;
    } catch (e) {
      const ms = nowMs() - t0;
      const aborted = isMatchSchedulingAbortError(e);
      const timedOut = isMatchSchedulingLikelyTimeout(e);
      const status = e?.response?.status;
      const body = e?.response?.data;

      if (aborted) {
        if (verbose) {
          console.info(`${LOG_PREFIX} GET state cancelled after ${Math.round(ms)}ms`, { matchId });
        } else {
          console.debug(`${LOG_PREFIX} GET state cancelled after ${Math.round(ms)}ms (overlap or navigation — not a HTTP error)`);
        }
      } else if (timedOut) {
        console.warn(`${LOG_PREFIX} GET state TIMEOUT after ${Math.round(ms)}ms — server may still be working; check Render logs`, {
          matchId,
        });
      } else {
        console.error(`${LOG_PREFIX} GET state FAILED after ${Math.round(ms)}ms`, {
          matchId,
          teamId,
          httpStatus: status,
          message: body?.message,
        });
      }
      throw e;
    }
  },

  vote: async (matchId, teamId, slotId, response) => {
    const t0 = nowMs();
    const verbose = matchSchedulingVerbose();
    const path = `/match-scheduling/${matchId}/vote`;
    try {
      if (verbose) console.info(`${LOG_PREFIX} POST ${path}`, { slotId, response });
      const data = await api.post(
        path,
        matchSchedulingAPI.getRequestBody({ slot_id: slotId, response }, teamId)
      );
      if (verbose) console.info(`${LOG_PREFIX} vote OK ${Math.round(nowMs() - t0)}ms`, data);
      return data;
    } catch (e) {
      console.error(`${LOG_PREFIX} vote FAILED ${Math.round(nowMs() - t0)}ms`, e?.response?.status, e?.response?.data);
      throw e;
    }
  },

  contestSoftSchedule: async (matchId, teamId) => {
    const t0 = nowMs();
    const verbose = matchSchedulingVerbose();
    const path = `/match-scheduling/${matchId}/contest-soft-schedule`;
    try {
      if (verbose) console.info(`${LOG_PREFIX} POST ${path}`);
      const data = await api.post(path, matchSchedulingAPI.getRequestBody({}, teamId));
      if (verbose) console.info(`${LOG_PREFIX} contest OK ${Math.round(nowMs() - t0)}ms`, data);
      return data;
    } catch (e) {
      console.error(`${LOG_PREFIX} contest FAILED ${Math.round(nowMs() - t0)}ms`, e?.response?.status, e?.response?.data);
      throw e;
    }
  },

  getAvailabilitySuggestions: async (matchId, teamId) => {
    const path = `/match-scheduling/${matchId}/availability-suggestions`;
    const cfg = matchSchedulingAPI.getRequestConfig(teamId) || {};
    return api.get(path, cfg);
  },

  confirmAvailabilitySlot: async (matchId, teamId, date, time) => {
    const path = `/match-scheduling/${matchId}/confirm-availability-slot`;
    return api.post(path, matchSchedulingAPI.getRequestBody({ date, time }, teamId));
  },
};

export default matchSchedulingAPI;
